'use client'

import { Play } from 'lucide-react'
import React, { useState } from 'react'

/** Converte URL do YouTube/Vimeo em embed; mp4/webm toca em `<video>`. */
export function resolverEmbedVideo(url: string): { tipo: 'iframe' | 'video'; src: string } | null {
  const u = url.trim()
  if (!u) return null
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/)
  if (yt) return { tipo: 'iframe', src: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0` }
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vm) return { tipo: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}?autoplay=1` }
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u)) return { tipo: 'video', src: u }
  if (/^https?:\/\//i.test(u)) return { tipo: 'iframe', src: u }
  return null
}

type Props = {
  videoUrl?: string | null
  capa: React.ReactNode
  titulo?: string
  className?: string
}

/** Capa com botão de play; ao clicar, o vídeo substitui a capa. */
export function VideoInline({ videoUrl, capa, titulo = 'Vídeo', className }: Props) {
  const [aberto, setAberto] = useState(false)
  const embed = videoUrl ? resolverEmbedVideo(videoUrl) : null

  if (!embed) return <div className={className}>{capa}</div>

  return (
    <div className={className}>
      {aberto ? (
        embed.tipo === 'video' ? (
          <video className="absolute inset-0 h-full w-full object-cover" src={embed.src} autoPlay controls playsInline />
        ) : (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={embed.src}
            title={titulo}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        )
      ) : (
        <button
          type="button"
          onClick={() => setAberto(true)}
          className="group absolute inset-0 h-full w-full cursor-pointer"
          aria-label={`Reproduzir: ${titulo}`}
        >
          {capa}
          <span
            className="absolute left-1/2 top-1/2 flex h-[56px] w-[56px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-105 md:h-[60px] md:w-[60px] lg:h-[103px] lg:w-[103px]"
            style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <span className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white text-black lg:h-[80px] lg:w-[80px]">
              <Play className="ml-1 h-5 w-5 lg:h-8 lg:w-8" fill="currentColor" strokeWidth={0} />
            </span>
          </span>
        </button>
      )}
    </div>
  )
}
